import * as phobos from '@atlassiansox/phobos-client-ts';

const BASE_URL = `${window.API_URL_OVERRIDE || __API_URL__}`;
const COMMON_HEADERS = { 'X-TOME-CLIENT': 'TOMEUI' };

const apiPath = (path: string) => `${BASE_URL}/api${path}`;

//  Custom error class for network errors
export class FetchError extends Error {
  constructor(public response: Response, message?: string, public title?: string) {
    super(message);
  }
}

// Fetch or post JSON from endpoint and throw a FetchError on error
//
// If an object is passed into options.body, it is JSON stringified and
// the Content-Type header set to 'application/json'
async function apiFetch(path: string, options?: RequestInit) {
  const { headers: optionHeaders, ...fetchOptions } = { ...options };
  let headers: HeadersInit = { ...COMMON_HEADERS, ...(optionHeaders || {}) };
  if (options?.body && typeof options.body === 'object') {
    fetchOptions.body = JSON.stringify(options.body);
    headers = { 'Content-Type': 'application/json', ...headers };
  }

  let response;

  try {
    response = await fetch(apiPath(path), {
      credentials: 'include',
      headers,
      ...(fetchOptions ?? {}),
    });
  } catch (error) {
    // This is where the annoying 'Failed to fetch' error message is coming from
    // Populate as a 400 error so this is not surfaced to sentry
    // @see useMessageErrorController
    throw new FetchError(new Response(error?.message || 'Network Error', { status: 400 }));
  }

  const data = await response.json();

  if (!response.ok) {
    throw new FetchError(response, data?.message);
  }

  return data;
}

type ApiPromise<T> = Promise<T>;

// Wrap a promise from the generated API client. If an exception is caught,
// either convert a network error to a FetchError and throw, or rethrow the
// original exception.
async function apiResolve<T>(promise: ApiPromise<T>, parseJsonError?: boolean) {
  //  Try to parse a JSON error object with a message field from the response by default
  if (parseJsonError === undefined) {
    parseJsonError = true;
  }

  try {
    const response = await promise;
    return response;
  } catch (err) {
    if (err instanceof Response) {
      const errorData = parseJsonError ? await err.json() : null;
      throw new FetchError(err, errorData?.message);
    }

    if (err instanceof phobos.ResponseError) {
      const errorData = parseJsonError ? await err.response.json() : null;
      throw new FetchError(err.response, errorData?.message);
    }

    throw err;
  }
}

const apiConfig = new phobos.Configuration({
  basePath: BASE_URL,
  credentials: 'include',
  headers: {
    ...COMMON_HEADERS,
  },
});

//  Instantiate generated Phobos client API's with configuration/credentials
const apis = {
  collection: new phobos.CollectionApi(apiConfig),
  team: new phobos.TeamEntityApi(apiConfig),
  product: new phobos.ProductEntityApi(apiConfig),
  capability: new phobos.CapabilityEntityApi(apiConfig),
  slo: new phobos.SLOEntityApi(apiConfig),
  slogroup: new phobos.SLOGroupApi(apiConfig),
  user: new phobos.AtlassianUsernameLookupApi(apiConfig),
  opsgenieTeam: new phobos.AtlassianOpsgenieTeamLookupApi(apiConfig),
  hot: new phobos.HOTEntityApi(apiConfig),
  signalFlow: new phobos.SignalFlowResourceApi(apiConfig),
  ufoResource: new phobos.UFOResourceApi(apiConfig),
  event: new phobos.EventEntityApi(apiConfig),
};

export { apiPath, apiFetch, apiResolve, apis };
